import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { getPublishedPost } from "@/lib/blog.functions";
import { ChevronLeft } from "lucide-react";
import { responsiveImage } from "@/lib/responsive-image";
import { absoluteUrl } from "@/lib/absolute-url";

const SITE_URL = "https://elvion-group.nl";

const postQO = (slug: string) =>
  queryOptions({
    queryKey: ["blog", "post", slug],
    queryFn: () => getPublishedPost({ data: { slug } }),
  });

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData(postQO(params.slug));
    if (!data.post) throw notFound();
    return data;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData?.post) return {};
    const p = loaderData.post;
    const url = `${SITE_URL}/blog/${params.slug}`;
    let description = (p.seo_description || p.excerpt || p.title).trim();
    if (description.length < 50) {
      description = `${description} — lees het volledige artikel op de Elvion Group Nederland blog over installatietechniek en beveiliging.`.slice(0, 160);
    } else {
      description = description.slice(0, 160);
    }
    const seoTitle = p.seo_title?.trim() || null;
    const published = p.published_at ?? p.created_at;
    const cover = absoluteUrl(p.cover_url);
    const articleLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: p.title,
      ...(published ? { datePublished: published } : {}),
      ...(p.author_name ? { author: { "@type": "Person", name: p.author_name } } : {}),
      ...(cover ? { image: [cover] } : {}),
      mainEntityOfPage: url,
    };
    return {
      meta: [
        { title: seoTitle ?? `${p.title} — Elvion Group Nederland` },
        { name: "description", content: description },
        { property: "og:title", content: seoTitle ?? p.title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...(cover ? [{ property: "og:image", content: cover }, { name: "twitter:image", content: cover }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(articleLd),
        },
      ],
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold">Artikel niet gevonden</h1>
        <Link to="/blog" className="mt-4 inline-block text-[var(--sky)] underline">
          Terug naar blog
        </Link>
      </main>
      <Footer />
    </>
  ),
  errorComponent: ({ error }) => (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold">Er ging iets mis</h1>
        <p className="mt-2 text-text-secondary">{error instanceof Error ? error.message : "Onbekende fout"}</p>
      </main>
      <Footer />
    </>
  ),
});

function fmtDate(s: string | null) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function BlogPostPage() {
  const { data } = useSuspenseQuery(postQO(Route.useParams().slug));
  const p = data.post!;
  const isHtml = /^\s*</.test(p.content);

  return (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1 text-sm font-bold text-[var(--sky)] hover:text-primary"
        >
          <ChevronLeft className="size-4" /> Terug naar blog
        </Link>

        <article className="mt-6">
          <header className="mb-6">
            <div className="text-xs font-bold uppercase tracking-wider text-[var(--sky)]">
              {fmtDate(p.published_at ?? p.created_at)}
              {p.author_name ? ` · ${p.author_name}` : ""}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              {p.title} <span className="text-text-secondary">— Elvion Groep</span>
            </h1>
            {p.excerpt && (
              <p className="mt-3 text-lg text-text-secondary">{p.excerpt}</p>
            )}
          </header>

          {p.cover_url && (() => {
            const r = responsiveImage(p.cover_url, [640, 960, 1280, 1600]);
            return (
              <img
                src={r?.src ?? p.cover_url}
                srcSet={r?.srcSet}
                sizes="(min-width: 1024px) 960px, 100vw"
                alt={p.title}
                loading="lazy"
                decoding="async"
                className="mb-8 w-full rounded-2xl object-cover"
              />
            );
          })()}

          {isHtml ? (
            <div
              className="article-content max-w-none text-base leading-relaxed text-foreground"
              dangerouslySetInnerHTML={{ __html: p.content }}
            />
          ) : (
            <div className="max-w-none space-y-5 text-base leading-relaxed text-foreground">
              {p.content.split(/\n{2,}/).map((para, i) => (
                <p key={i} className="whitespace-pre-wrap">
                  {para}
                </p>
              ))}
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}
